import { SnackBarService } from './../../../../core/services/snack-bar.service';
import { WjMenu } from '@grapecity/wijmo.angular2.input';
import * as wjcGrid from '@grapecity/wijmo.grid';
import * as wjcGridSheet from '@grapecity/wijmo.grid.sheet';
// Angular
import { AfterViewInit, Component, ElementRef, Inject, NgZone, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { PlatformQueryService } from '@core/services/platform-query.service';
import { ColumnPaginationOption, RowPaginationOption, SpreadSheetEvent, SpreadSheetEventModel } from "@features/spreadsheet/models/spread-sheet";
import { FlexSheetCellMenuService } from "@features/spreadsheet/services/flex-sheet-cell-menu.service";
import { FlexSheetService } from '@features/spreadsheet/services/flex-sheet.service';
import { PlatformToolSpreadsheetHandler } from '@features/spreadsheet/services/platform-tool-spreadsheet-handler';
import { PlatformCommand } from '@shared/commands/command-base';
import { Api } from '@shared/constants/common-field.constants';
import { RootDynamicComponentHelper } from "@shared/helpers/root-dynamic-component-helper";
import { PlatformQuery } from '@shared/models/platform-query/platform-query';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { FlexSheetDataService } from '../../services/flex-sheet-data.service';
import { CommandService } from '@core/services/command.service';
import { FuseSplashScreenService } from '@barebone-fuse/@fuse/services/splash-screen.service';
import { FlexSheetNavigationService } from '@features/spreadsheet/services/flex-sheet-navigation.service';
import { FlexSheetCellClickService } from '@features/spreadsheet/services/flex-sheet-cell-click.service';
import {DetailsInstructionService} from "@shared/services/details-instruction-service";
import { DOCUMENT } from '@angular/common';

@Component({
    standalone: false,
    selector: 'app-workbook-container',
  templateUrl: './workbook-container.component.html',
  styleUrls: ['./workbook-container.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class WorkbookContainerComponent implements OnInit, AfterViewInit, OnDestroy {
  // Host div the manually-constructed FlexSheet is attached to.
  @ViewChild('flexSheetHost', { static: false }) flexSheetHost: ElementRef<HTMLDivElement>;
  // FlexSheet instance, constructed in ngAfterViewInit outside the Angular zone.
  // Declared as a public property so template refs like `flexSheet.selectedSheetIndex` resolve to it.
  flexSheet: wjcGridSheet.FlexSheet | any;
  @ViewChild('ctxMenu') flexSheetCtxMenu: WjMenu;
  undoStack: wjcGridSheet.UndoStack;
  sortManager: wjcGridSheet.SortManager;
  selection: any = {
    content: '',
    position: '',
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontSize: '8px'
  };
  isRowPaginationOpen = false;
  isColumnPaginationOpen = false;
  updatingSelection = false;
  updateWithImport = false;
  jumpToRow;
  jumpToCol;
  closeMenu = { cmd: 'Close', iconClass: 'fa-solid fa-xmark', label: 'Close' }
  private ngUnsubscribe = new Subject<void>();
  private explainModeClassObserver: MutationObserver | null = null;
  private selectionModeBeforeExplain: wjcGrid.SelectionMode | null = null;
  private explainSelectionModeRestoreTimer: ReturnType<typeof setTimeout> | null = null;
  // Slightly longer than the directive's idle timeout (60s) so normal flow runs first.
  private static readonly EXPLAIN_SELECTION_MODE_RESTORE_TIMEOUT_MS = 75000;

  constructor(public flexSheetDataService: FlexSheetDataService,
    private platformQueryService: PlatformQueryService,
    private commandService: CommandService,
    public flexSheetService: FlexSheetService,
    private platformToolSpreadsheetHandler: PlatformToolSpreadsheetHandler,
    private fuseSplashScreenService: FuseSplashScreenService,
    public flexSheetCellMenuService: FlexSheetCellMenuService,
    public snackBarService: SnackBarService,
    public flexSheetNavigationService: FlexSheetNavigationService,
    public flexSheetCellClickService: FlexSheetCellClickService,
    public detailsInstructionService: DetailsInstructionService,
    private ngZone: NgZone,
    @Inject(DOCUMENT) private documentRef: Document,
  ) {
  }

  onToggleRowPagination() {
    this.isRowPaginationOpen = !this.isRowPaginationOpen;
  }

  onToggleColumnPagination() {
    this.isColumnPaginationOpen = !this.isColumnPaginationOpen;
  }

  disableContextMenu(event: MouseEvent) {
    this.flexSheetCellClickService.onContextMenu(event, this.flexSheetCtxMenu)
  }


  setDefaultPagination(worksheetIndex: number) {
    var worksheetId = this.flexSheetService.getWorksheets()[worksheetIndex].id;

    var currentWorkSheetPaginationModelFromMagicLink = this.flexSheetService.getMagicLinkModel().tabBar.tabs[0].tabData[worksheetId]?.paginationModel;
    this.flexSheetService.getMagicLinkModel().tabBar.tabs[0].tabData['CurrentGridColumn'];

    if (currentWorkSheetPaginationModelFromMagicLink) {
      this.flexSheetNavigationService.setPagination(
        currentWorkSheetPaginationModelFromMagicLink.currentGridColumn || 0,
        currentWorkSheetPaginationModelFromMagicLink.currentGridRow || 0,
        this.flexSheetService.getWorksheets()[worksheetIndex].id
      );
      return;
    }


    var currentSheetLastPaginationModel = this.flexSheetNavigationService.getSheetPagination(worksheetId);
    this.flexSheetNavigationService.setPagination(
      currentSheetLastPaginationModel?.currentGridColumn || 0,
      currentSheetLastPaginationModel?.currentGridRow || 0,
      worksheetId
    );
  }

  ngAfterViewInit(): void {
    // Construct FlexSheet outside Angular's zone so its internal document-level
    // listeners (mousedown/keydown/scroll/etc.) are not patched by zone.js.
    // This prevents every mouse/key event anywhere in the app from triggering
    // a global CD sweep via the FlexSheet, which was causing dialog input lag.
    this.ngZone.runOutsideAngular(() => this.initFlexSheet());

    if (this.flexSheetService.getFlexSheet()?.selectedSheetIndex) this.setDefaultPagination(this.flexSheetService.getFlexSheet().selectedSheetIndex);
    // this.navigationService.setPagination(0, 0);
    this.flexSheetCellMenuService.setFlexSheetContextMenu(this.flexSheetCtxMenu)

    const link = document.querySelector('a[href*="https://developer.mescius.com/licensing/wijmo?utm_source=Wijmo-In-App&utm_medium=Click-to-Site&utm_campaign=Wijmo-User-Analysis"]');

    if (link) {
      // Hide the element
      //@ts-ignore-next-line
      link.style.display = 'none';
    }
    // this.adjustSize();
    this.setupExplainModeObserver();
  }

  private initFlexSheet(): void {
    const hostEl = this.flexSheetHost?.nativeElement;
    if (!hostEl) return;

    const fs = new wjcGridSheet.FlexSheet(hostEl);
    fs.selectionMode = wjcGrid.SelectionMode.MultiRange;

    // Pre-populate sheets for each worksheet (previously done by <wj-sheet> *ngFor).
    const worksheets = this.flexSheetService.getWorksheets() || [];
    for (const ws of worksheets) {
      fs.sheets.push(new wjcGridSheet.Sheet(undefined, undefined, ws?.name));
    }

    // Host-level click (replaces Angular (click) binding). Re-enter the zone
    // because onCellClick opens the sidebar + updates Angular state.
    hostEl.addEventListener('click', (e: MouseEvent) => {
      if (this.documentRef?.body?.classList.contains('xatbot-explain-selection-active')) {
        return;
      }
      this.ngZone.run(() => this.flexSheetCellClickService?.onCellClick(e));
    });

    // Host-level contextmenu (replaces (contextmenu) + [wjContextMenu] directive).
    // Mirrors Wijmo's WjContextMenu directive: set owner, call show(e).
    hostEl.addEventListener('contextmenu', (e: MouseEvent) => {
      if (this.documentRef?.body?.classList.contains('xatbot-explain-selection-active')) {
        return;
      }
      this.ngZone.run(() => {
        const menu = this.flexSheetCtxMenu;
        if (menu) {
          e.preventDefault();
          e.stopPropagation();
          menu.owner = hostEl;
          menu.show(e);
        }
        this.flexSheetCellClickService.onContextMenu(e, this.flexSheetCtxMenu);
      });
    });

    this.flexSheet = fs;
    // flexInitialized updates Angular-bound state (selection, sort manager, etc.).
    this.ngZone.run(() => this.flexInitialized(fs));
  }

  async ngOnInit() {
    console.log('[Excel] NgOnInit Workbook container');
    this.platformToolSpreadsheetHandler.flexSheetService = this.flexSheetService;
    this.platformToolSpreadsheetHandler.openWorksheet$ = new Subject<void>();
    this.platformToolSpreadsheetHandler.openWorksheet$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(async (workbookInfo: any) => {
      const workbookId = workbookInfo.workbookId;
      const toLoadWorksheetId = workbookInfo.worksheetId;

      if (workbookId == null || toLoadWorksheetId == null) {
        return;
      }

      if (toLoadWorksheetId && this.flexSheetService.getCurrentWorkSheet() && this.flexSheetService.getCurrentWorkSheet().id != toLoadWorksheetId) {
        console.log('[Excel] toLoadWorksheetId ' + toLoadWorksheetId);
        await this.loadStageAreaWorksheet(workbookId, toLoadWorksheetId); // for staging area
      } else {
        // const worksheet = this.flexSheetService.getWorksheets().find(ws => ws.id === toLoadWorksheetId);
        // if (worksheet) {
        // this.flexSheetService.onWorksheetChange(worksheet);
        await this.flexSheetService.refreshSelectedWorkSheet();
        // }
      }
    });
  }

  async loadStageAreaWorksheet(currentWorkbookId: string, toLoadWorksheetId: string) {
    const query = new PlatformQuery();
    query.name = 'WorksheetsQuery';
    query.api = this.flexSheetService.getApi();
    query.fieldValues['WorkbookId'] = currentWorkbookId;
    query.fieldValues['WorkbookSetId'] = this.flexSheetService.getCurrentWorkBookSetId();
    const response = await this.platformQueryService.executeQuery(query).toPromise();
    const ws = response.fieldValues['WorksheetModels'];

    if (ws && ws.length > 0) {
      const loadedWorksheet = ws.find(x => x.id === toLoadWorksheetId);
      if (loadedWorksheet) {
        const f = this.flexSheetService.getWorksheets().find(x => x.id === toLoadWorksheetId);
        if (!f) {
          this.flexSheetService.addWorksheet(loadedWorksheet);
          // Replaces the removed <wj-sheet> *ngFor.
          this.flexSheet?.sheets.push(new wjcGridSheet.Sheet(undefined, undefined, loadedWorksheet.name));
        }
        this.flexSheetService.getFlexSheet().selectedSheetIndex = this.flexSheetService.getWorksheets().findIndex(x => x.id === toLoadWorksheetId);
        // await this.flexSheetService.onWorksheetChange(loadedWorksheet);
        // await this.flexSheetService.refreshSelectedWorkSheet();
      }
    }

  }


  getCurrentSheetPaginationModel(currentWorksheetIndex: number) {
    const worksheets = this.flexSheetService.getWorksheets();
    if (worksheets && worksheets.length > currentWorksheetIndex) {
      const id = worksheets[currentWorksheetIndex].id;
      const tabBar = this.flexSheetService.getMagicLinkModel()?.tabBar;
      if (tabBar && tabBar.tabs[0] && tabBar.tabs[0].tabData[id]) {
        return tabBar.tabs[0].tabData[id].paginationModel;
      }
    }
    return null;
  }

  async flexInitialized(flexSheet: wjcGridSheet.FlexSheet) {
    if (flexSheet) {
      this.flexSheetService.setFlexSheet(flexSheet);
      this.undoStack = this.flexSheetService.getFlexSheet().undoStack;
      const currentWorksheetIndex = this.setDefaultIndex();
      this.flexSheetService.getFlexSheet().selectedSheetIndex = currentWorksheetIndex;

      var currentSheet = this.flexSheetService.getWorksheets()[currentWorksheetIndex];

      var currentSheetLastPaginationModel = this.flexSheetNavigationService.getSheetPagination(currentSheet.id);

      await this.flexSheetService.onWorksheetChange({
        worksheet: this.flexSheetService.getWorksheets()[currentWorksheetIndex],
        gridCol: currentSheetLastPaginationModel?.currentGridColumn || 0,
        gridRow: currentSheetLastPaginationModel?.currentGridRow || 0,
        row: currentSheetLastPaginationModel?.selectedRow || 0,
        col: currentSheetLastPaginationModel?.selectedCol || 0
      });

      this.flexSheetService.getFlexSheet().deferUpdate(() => {
        this.sortManager = this.flexSheetService.getFlexSheet().sortManager;
        this.flexSheetService.updateSelection(this.flexSheetService.getFlexSheet().selection);
      });
    } else {
      console.log('[Overwrite Log] flexInitialized: flex sheet NOT defined — workbook container init skipped');
    }
  }

  refresh() {
    const allCommands = this.flexSheetService.getMagicLinkModel().breadcrumbBar.items.map(o => o.commands).flat(1);
    var autoCalculateCommand = allCommands.find(o => o.title === 'Calculate Now');
    var autoCalculateCommandCloned = Object.assign({}, autoCalculateCommand);
    autoCalculateCommandCloned.fieldValues = Object.assign({}, autoCalculateCommand.fieldValues);
    autoCalculateCommandCloned.fieldValues["RefreshClicked"] = true;
    if (autoCalculateCommandCloned.fieldValues['CommandHandler']) {
      const commandHandler = RootDynamicComponentHelper.getComponent(autoCalculateCommandCloned.fieldValues['CommandHandler']);
      if (commandHandler) {
        commandHandler.executeCommand(autoCalculateCommandCloned, this.platformToolSpreadsheetHandler);
        return;
      }
    }
    this.platformToolSpreadsheetHandler.executeCommand(autoCalculateCommandCloned, () => {
      this.flexSheetService.unSavedCount = 0;
    })
    // this.flexSheetService.onWorksheetChange(this.flexSheetService.getCurrentWorkSheet(), false);
    // this.flexSheetService.isWorkbookSetUpdated = false;
  }

  async addMoreDataIntoSheet(data: any, numOfRows: number) {
    const more = await this.getSheetDataBySize(numOfRows, data.length);
    for (let i = 0; i < more.length; i++) {
      data.push(more[i]);
    }
  }

  async getSheetDataBySize(numOfRows: number, start?: number) {

    return [];
  }

  onWorksheetChange(index: number) {
    this.flexSheetService.calculateNowManager(async (c: any) => {
      this.setDefaultPagination(index)

      this.flexSheetService.getFlexSheet().selectedSheetIndex = index;
      this.flexSheetService.spreadSheetEvent$.next(new SpreadSheetEventModel(SpreadSheetEvent.WorkSheetChanged, this.flexSheetService.getWorksheets()[index]));
    }, index);
  }

  removeSourceFileCommand() {
    let command = new PlatformCommand('AddEventSourceModelCommand', this.flexSheetService.getApi());
    command.fieldValues['WorkbookId'] = this.flexSheetService.getCurrentWorkBook().id;
    command.fieldValues['CommandName'] = 'RemoveSourceFileCommand';
    command.fieldValues['EventSourceId'] = this.flexSheetService.getCurrentWorkBookSetId();
    command.fieldValues['WorkbookSetId'] = this.flexSheetService.getCurrentWorkBookSetId();
    command.fieldValues['SourceType'] = 'WorkbookSet';
    this.platformToolSpreadsheetHandler.executeCommand(command);
    this.flexSheetService.getCurrentWorkBook().isPlaceHolder = true;
  }

  configureWorksheetPlaceHolder() {
    const command = new PlatformCommand('VirtualPlaceHolderWorksheetMappingCommand', this.flexSheetService.getApi());
    command.fieldValues['WorkbookId'] = this.flexSheetService.getCurrentWorkBook().id;
    command.fieldValues['WorksheetId'] = this.flexSheetService.getCurrentWorkSheet().id;
    // command.fieldValues['CommandName'] = 'VirtualPlaceHolderWorksheetMappingCommand';
    command.fieldValues['EventSourceId'] = this.flexSheetService.getCurrentWorkBookSetId();
    command.fieldValues['WorkbookSetId'] = this.flexSheetService.getCurrentWorkBookSetId();
    command.fieldValues['SourceType'] = 'WorkbookSet';
    this.platformToolSpreadsheetHandler.executeCommand(command);
  }

  ngOnDestroy(): void {
    this.cleanupExplainModeObserver();
    this.restoreWorkbookSelectionMode();
    this.flexSheetService.onDestroy();
    if (this.platformToolSpreadsheetHandler.openWorksheet$) {
      this.platformToolSpreadsheetHandler.openWorksheet$.complete();
    }
    this.ngUnsubscribe.complete();
    this.flexSheet?.dispose?.();
    this.flexSheet = null;
  }

  close() {
    this.flexSheetCtxMenu.hide();
  }

  private setDefaultIndex() {
    let currentWorksheetIndex = 0;
    if (this.flexSheetService.getMagicLinkModel()?.tabBar?.tabs[0]?.tabData?.LoadWorksheet) {
      const id = this.flexSheetService.getMagicLinkModel()?.tabBar?.tabs[0]?.tabData?.LoadWorksheet;
      const index = this.flexSheetService.getWorksheets().findIndex(x => x.id === id);
      if (index >= 0) {
        currentWorksheetIndex = index;
      }
    }
    return currentWorksheetIndex;
  }

  private setupExplainModeObserver(): void {
    const body = this.documentRef?.body;
    if (!body) {
      return;
    }

    const syncExplainMode = () => {
      const active = body.classList.contains('xatbot-explain-selection-active');
      this.applyExplainSelectionMode(active);
    };

    this.explainModeClassObserver?.disconnect();
    this.explainModeClassObserver = new MutationObserver(syncExplainMode);
    this.explainModeClassObserver.observe(body, { attributes: true, attributeFilter: ['class'] });
    syncExplainMode();
  }

  private cleanupExplainModeObserver(): void {
    this.explainModeClassObserver?.disconnect();
    this.explainModeClassObserver = null;
  }

  private applyExplainSelectionMode(active: boolean): void {
    if (!this.flexSheet) {
      return;
    }

    if (active) {
      if (this.selectionModeBeforeExplain === null) {
        this.selectionModeBeforeExplain = this.flexSheet.selectionMode;
      }
      this.flexSheet.selectionMode = wjcGrid.SelectionMode.None;
      this.scheduleSelectionModeRestore();
      return;
    }

    this.restoreWorkbookSelectionMode();
  }

  private scheduleSelectionModeRestore(): void {
    if (this.explainSelectionModeRestoreTimer) {
      clearTimeout(this.explainSelectionModeRestoreTimer);
    }
    this.explainSelectionModeRestoreTimer = setTimeout(() => {
      this.explainSelectionModeRestoreTimer = null;
      this.restoreWorkbookSelectionMode();
    }, WorkbookContainerComponent.EXPLAIN_SELECTION_MODE_RESTORE_TIMEOUT_MS);
  }

  private restoreWorkbookSelectionMode(): void {
    if (this.explainSelectionModeRestoreTimer) {
      clearTimeout(this.explainSelectionModeRestoreTimer);
      this.explainSelectionModeRestoreTimer = null;
    }

    if (!this.flexSheet || this.selectionModeBeforeExplain === null) {
      return;
    }

    this.flexSheet.selectionMode = this.selectionModeBeforeExplain;
    this.selectionModeBeforeExplain = null;
  }

  searchWorksheet() {
    const command = new PlatformCommand('SearchWorkbookCommand', Api.Spreadsheet);
    command.fieldValues['WorkbookId'] = this.flexSheetService.getCurrentWorkBook().id;
    command.fieldValues['WorksheetId'] = this.flexSheetService.getCurrentWorkSheet().id;
    command.fieldValues['EventSourceId'] = this.flexSheetService.getCurrentWorkBookSetId();
    command.fieldValues['WorkbookSetId'] = this.flexSheetService.getCurrentWorkBookSetId();

    if (this.flexSheetService.getActiveCell().rng) {
      command.fieldValues['Col'] = this.flexSheetService.getActiveCell().rng.col;
      command.fieldValues['Row'] = this.flexSheetService.getActiveCell().rng.row;
    } else {
      command.fieldValues['Col'] = this.flexSheetService.getActiveCell().col;
      command.fieldValues['Row'] = this.flexSheetService.getActiveCell().row;
    }

    this.platformToolSpreadsheetHandler.executeCommand(command);
  }



  async jumpToPaginatedGrid(type: string) {
    let row, col, cellAddress;
    const sheetId = this.flexSheetService.getCurrentWorkSheet().id;
    const pagination = this.flexSheetNavigationService.getSheetPagination(sheetId);

    switch (type) {
        case "Row":
            if (isNaN(Number(this.jumpToRow))) {
                this.snackBarService.showError('Please enter a valid row number');
                return;
            }
            row = Number(this.jumpToRow - 1);
            const columnOption = pagination.columnPaginationOptions.find(
                (columnOption: ColumnPaginationOption) => {
                    return columnOption.gridColumn === pagination.currentGridColumn;
                }
            );
            col = Number(columnOption.startColumnIndex);
            cellAddress = null;
            break;
        case "Col":
            col = this.jumpToCol;
            const rowOption = pagination.rowPaginationOptions.find(
                (rowOption: RowPaginationOption) => {
                    return rowOption.gridRow === pagination.currentGridRow;
                }
            );
            row = Number(rowOption.startRowIndex);
            cellAddress = `${col}${row + 1}`;
            row = -1; col = -1;
            break;
    }


    this.flexSheetNavigationService.executeNavigateToPaginatedPageByCellCommand(this.flexSheetService.getCurrentWorkSheet().id,row,col,cellAddress,()=>{
      if(type === "Row"){
        this.isRowPaginationOpen = false;
        this.jumpToRow = undefined;
      }else if(type === "Col"){
        this.isColumnPaginationOpen = false;
        this.jumpToCol = undefined;
      }
      this.fuseSplashScreenService.hide();
    });
  }


  navigatePaginatedColumns(gridCol: number) {
    this.isColumnPaginationOpen = false;
    this.flexSheetNavigationService.navigatePaginatedColumns(gridCol);
  }
  navigatePaginatedRows(gridRow: number) {
    this.isRowPaginationOpen = false;
    this.flexSheetNavigationService.navigatePaginatedRows(gridRow);
  }
}

